const configured = import.meta.env.VITE_STORE_API_URL?.replace(/\/$/, '');
const base = import.meta.env.DEV ? '/local-affiliates' : configured;
const referralKey = 'rica-tanto-referral';
const referralCodeKey = 'rica-tanto-referral-code';
type Referral = { token: string; expires: number };
let memoryReferral: Referral | undefined;
let pending: Promise<void> = Promise.resolve();
let retryCode: string | undefined;

export async function affiliateRequest(body: Record<string, unknown>) {
  if (!base) throw new Error('O programa de afiliados ainda não está disponível.');
  const response = await fetch(`${base}/affiliates.php`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data) throw new Error(data?.message || 'Não foi possível acessar o programa. Tente novamente mais tarde.');
  return data;
}

function validReferral(value: unknown): value is Referral {
  if (!value || typeof value !== 'object') return false;
  const ref = value as Partial<Referral>;
  return typeof ref.token === 'string' && /^[a-f0-9]{64}$/.test(ref.token)
    && typeof ref.expires === 'number' && ref.expires * 1000 > Date.now();
}

async function requestReferral(code: string): Promise<void> {
  const data = await affiliateRequest({ action: 'capture', code });
  if (validReferral(data.referral)) {
    memoryReferral = data.referral;
    try { localStorage.setItem(referralKey, JSON.stringify(data.referral)); } catch { /* Mantém em memória nesta visita. */ }
  }
  // Só uma resposta válida troca a indicação. Código inválido preserva a anterior.
  retryCode = undefined;
  try { localStorage.removeItem(referralCodeKey); } catch { /* Armazenamento indisponível. */ }
}

export function captureReferral(code: string): Promise<void> {
  if (!/^[a-z0-9-]{3,40}$/.test(code)) return pending;
  pending = pending.then(async () => {
    retryCode = code;
    // Guarda apenas tentativas ainda não confirmadas, para uma nova tentativa breve.
    try { localStorage.setItem(referralCodeKey, JSON.stringify({ code, at: Date.now(), pending: true })); } catch { /* Mantém em memória. */ }
    await requestReferral(code);
  }).catch(() => { /* A vitrine continua funcionando; o checkout tenta de novo. */ });
  return pending;
}

export async function getReferralToken(): Promise<string | undefined> {
  await pending;
  if (!retryCode) {
    try {
      const saved = JSON.parse(localStorage.getItem(referralCodeKey) || 'null');
      // Não renova uma indicação vencida a partir de códigos antigos do navegador.
      if (saved?.pending === true && typeof saved.code === 'string' && /^[a-z0-9-]{3,40}$/.test(saved.code)
        && typeof saved.at === 'number' && saved.at <= Date.now() && Date.now() - saved.at < 15 * 60_000) retryCode = saved.code;
      else localStorage.removeItem(referralCodeKey);
    } catch { /* Armazenamento indisponível. */ }
  }
  if (retryCode) {
    try { await requestReferral(retryCode); }
    catch { throw new Error('Não foi possível registrar a indicação da sua compra. Tente finalizar novamente em instantes.'); }
  }
  if (validReferral(memoryReferral)) return memoryReferral.token;
  try {
    const saved = JSON.parse(localStorage.getItem(referralKey) || 'null');
    if (validReferral(saved)) return saved.token;
    localStorage.removeItem(referralKey);
  } catch { /* O servidor confere todo token recebido. */ }
  return undefined;
}
